import { Injectable, Inject } from '@angular/core';
import {
  Router,
  NavigationExtras,
  UrlTree,
  NavigationBehaviorOptions,
  ROUTES,
  Route,
  UrlSegment,
} from '@angular/router';
import { AppSettings } from '../shared/Constants/sharedConstants';

/**
 * Keeps a theme's path prefix (e.g. /acme/dashboard) attached to every navigation, so a themed
 * site behaves like its own site rather than dropping back to the unthemed URL on the first
 * router hop.
 *
 * The theme is resolved SERVER-SIDE, once, before Angular bootstraps: main.ts fetches
 * GetuiAppConfig?rq=<full href>, which matches the URL against the Theme table and returns the
 * slug in currentTheme.themePathSegment - non-null only when the URL genuinely sat under that
 * theme's CompanyUrl. This is an allow-list, and that matters: the previous implementation
 * derived the theme from the URL itself with the rule "any first segment I don't recognise as a
 * route is a theme", which adopted anything unknown as a permanent prefix. Because
 * Location.path() includes the query string, a reload of /login?reason=expired (where
 * AuthService sends every 401'd session) made the theme the literal string
 * 'login?reason=expired', and the app then spent the rest of the session on URLs like
 * /login%3Freason%3Dexpired/tank. Do not reintroduce URL-shape guessing here.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeRouter extends Router {
  private readonly currentTheme: string | null;
  private flattenedRoutes: string[] = [];

  constructor(@Inject(ROUTES) routes: Route[][]) {
    super();

    this.flattenRoutes(routes);
    this.currentTheme =
      AppSettings?.UIAppConfig?.currentTheme?.themePathSegment || null;
  }

  private flattenRoutes(routes: Route[][]) {
    const flattenRoute = (route: Route, parentPath: string = '') => {
      const path = parentPath + (route.path ? `/${route.path}` : '');
      if (route.path) {
        this.flattenedRoutes.push(route.path);
      }
      if (route.children) {
        route.children.forEach((child) => flattenRoute(child, path));
      }
    };

    routes.forEach((routeArray) => {
      routeArray.forEach((route) => flattenRoute(route));
    });
    // Remove duplicates and empty paths
    this.flattenedRoutes = [...new Set(this.flattenedRoutes)].filter(
      (route) => route !== '',
    );
  }

  navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    if (this.currentTheme && this.isKnownRoute(commands[0])) {
      commands = [this.currentTheme, ...commands];
    }
    return super.navigate(commands, extras);
  }

  createUrlTree(commands: any[], navigationExtras?: NavigationExtras): UrlTree {
    //add the theme to the url if the first segment is known and the relative path is root
    if (
      this.currentTheme &&
      navigationExtras?.relativeTo?.pathFromRoot?.length === 2 &&
      this.isKnownRoute(commands[0])
    ) {
      commands = [this.currentTheme, ...commands];
    }
    return super.createUrlTree(commands, navigationExtras);
  }

  navigateByUrl(
    url: string | UrlTree,
    extras?: NavigationBehaviorOptions,
  ): Promise<boolean> {
    //handle the case where the url is a string
    if (
      typeof url === 'string' &&
      this.currentTheme &&
      this.isKnownRoute(this.firstPathSegment(url))
    ) {
      url = `/${this.currentTheme}${url.startsWith('/') ? url : '/' + url}`;
    }
    //if the url is a UrlTree and the first segment is known, then add the theme to the url
    else if (url instanceof UrlTree && this.currentTheme) {
      // A tree for '/' or a query-only tree has no primary child at all.
      const segments = url.root.children['primary']?.segments;
      if (segments?.length && this.isKnownRoute(segments[0].path)) {
        segments.splice(0, 0, new UrlSegment(this.currentTheme, {}));
      }
    }
    return super.navigateByUrl(url, extras);
  }

  serializeUrl(url: UrlTree): string {
    const serialized = super.serializeUrl(url);
    if (
      this.currentTheme &&
      this.isKnownRoute(this.firstPathSegment(serialized))
    ) {
      return `/${this.currentTheme}${serialized.startsWith('/') ? serialized : '/' + serialized}`;
    }
    return serialized;
  }

  private isKnownRoute(segment: string): boolean {
    return this.flattenedRoutes.includes(segment);
  }

  /**
   * First path segment of a serialised URL, with any query string or fragment stripped -
   * '/dashboard?tab=1' is the 'dashboard' route, not a segment called 'dashboard?tab=1'.
   */
  private firstPathSegment(url: string): string {
    const path = url.split(/[?#]/)[0];
    return (path.startsWith('/') ? path.slice(1) : path).split('/')[0];
  }
}
